import { Timecode as TimecodeLib } from 'timecode';
import { xmlToJson, getElementValue as getVal } from './xmlUtils';
import RunContext from '../constants/runContext';

/**
 * Parses various messages to retrieve mosObj or item information.
 * @param {string} message Message to parse
 */
export const parseMosMessageXml = message => {
  const parsedMessage =
    message && (typeof message === 'string' || message instanceof String)
      ? xmlToJson(message)
      : message;
  const mosItem = getVal(parsedMessage.mos);
  let item = null;
  let url = null;

  if (mosItem.mosObj && !mosItem.objPaths) {
    item = parseMosObj(mosItem.mosObj);
    // Legacy support for ENPS, which has mosAbstract one level up from the mosObj
    item.mosAbstract = getVal(mosItem.mosAbstract) || item.mosAbstract;
    // Redirected MOS object from INEWS has correct mosObjId on level up from the mosObj
    item.objId = getVal(mosItem.objID) || item.objId;
  } else if (mosItem.ncsItem && mosItem.ncsItem[0].item) {
    item = parseItem(mosItem.ncsItem[0].item[0]);
  } else if (mosItem.mosItemReplace) {
    item = parseItem(mosItem.mosItemReplace[0].item[0]);
  } else if (mosItem.ncsAppInfo) {
    if (mosItem.ncsAppInfo[0].mosObj) {
      item = parseMosObj(mosItem.ncsAppInfo[0].mosObj);
    } else if (mosItem.ncsAppInfo[0].item) {
      // For reskinned objects, iNews sends the real reskinned object in the item
      // and the original in the mosObj
      if (mosItem.ncsAppInfo[0].item[0].mosObj && !mosItem.ncsAppInfo[0].item[0].mosAbstract) {
        item = parseMosObj(getVal(mosItem.ncsAppInfo[0].item[0].mosObj));
        item.itemId = getVal(mosItem.ncsAppInfo[0].item[0].itemID) || item.id;
        // Redirected MOS object from INEWS has correct mosObjId on level up from the mosObj
        item.objId = getVal(mosItem.ncsAppInfo[0].item[0].objID) || item.objId;
      } else {
        item = parseItem(mosItem.ncsAppInfo[0].item);
      }
    }
  } else {
    // Just try to parse the mos tag.
    item = mosItem.itemID ? parseItem(mosItem) : parseMosObj(mosItem);
  }

  if (!item) {
    console.error('Failed to parse mos xml.');
    return;
  }

  if (item.objPaths && !url) {
    url = getThumbnail(item.objPaths);
  }

  return {
    id: item.itemId || getVal(mosItem.itemID) || item.objId || null,
    ncsId: getVal(mosItem.ncsID) || null,
    mosObjectId: item.objId || null,
    thumbnailUrl: url,
    abstract: item.mosAbstract || item.slug,
    isFavorite: false,
    automationStart: item.automationStart,
    automationDuration: item.automationDuration,
    fileType: item.subtype || null,
    layer: item.layer || null
  };
};

const getThumbnail = objPaths => {
  let url = null;
  const jpg = objPaths.objProxyPath.find(x => x.$ && x.$.techDescription === 'JPEG Thumbnail');
  const png = objPaths.objProxyPath.find(x => x.$ && x.$.techDescription === 'PNG Proxy');
  url = png || jpg;
  return url ? url._.trim() : null;
};

/**
 * Parses mos ncsAppInfo messages.
 * @param {string} message Message to parse
 */
export const parseAppInfoMessage = message => {
  let ncsId = null;
  let mosId = null;
  let mosObj = null;

  const parsedMessage = xmlToJson(message);
  if (!parsedMessage || !parsedMessage.mos) {
    return null;
  }
  const mosItem = parsedMessage.mos;
  if (!mosItem.ncsAppInfo || !mosItem.ncsAppInfo[0].ncsInformation) {
    return;
  }

  const ncsAppInfo = getVal(mosItem.ncsAppInfo);
  const ncsInformation = parseNcsInformation(getVal(ncsAppInfo.ncsInformation));

  ncsId = getVal(mosItem.ncsID);
  mosId = getVal(mosItem.mosID);
  mosObj = ncsAppInfo.item || ncsAppInfo.mosObj ? parseMosMessageXml(message) : null;

  return {
    mosId,
    ncsId,
    mosObj,
    ncsInformation
  };
};

const parseNcsInformation = ncsInformationNode => {
  if (!ncsInformationNode) {
    return {};
  }

  const ncsInformation = {};

  Object.keys(ncsInformationNode).forEach(k => {
    switch (k) {
      case 'cameraPos':
        const cameraPos = getVal(ncsInformationNode[k]);
        ncsInformation[k] = Object.keys(cameraPos).map(v => getVal(cameraPos[v]));
        break;
      default:
        ncsInformation[k] = getVal(ncsInformationNode[k]);
        break;
    }
  });

  /* if (category) {
    ncsInformation.category = category;
  }

  if (context) {
    ncsInformation.context = context;
  }*/

  return ncsInformation;
};

/* const parseNode = (node) => {
  const keys = Object.keys(node);
  for (let i = 0; i < keys.length; i++) {
    const nodeVal = node[keys[i]];
    if (nodeVal.length > 1) {
      nodeVal
    }
    if (node[keys[i]])
  }
}*/

const parseMosExternalMetadata = mosMetadataNode => {
  if (!mosMetadataNode) {
    return {
      mosPayload: {}
    };
  }

  const mosExternalMetadata = getVal(mosMetadataNode);
  let mosPayload = null;
  let objectDetails = null;
  if (mosExternalMetadata.mosPayload) {
    mosPayload = getVal(mosExternalMetadata.mosPayload);

    if (mosPayload.ObjectDetails) {
      objectDetails = getVal(mosPayload.ObjectDetails);
    }
  }

  return {
    mosPayload: {
      automationStart: mosPayload ? getVal(mosPayload.sAVsom) : '00:00:00:00',
      automationDuration: mosPayload ? getVal(mosPayload.sAVeom) : '00:00:00:00',
      subtype: mosPayload ? getVal(mosPayload.subtype) : '',
      layer: objectDetails ? getVal(objectDetails.layer) : ''
    }
  };
};

const parseMosObj = mosObjNode => {
  if (!mosObjNode) {
    return;
  }

  const mosObj = getVal(mosObjNode);

  const mosExternalMetadata = parseMosExternalMetadata(mosObj.mosExternalMetadata);

  return {
    objId: getVal(mosObj.objID) || null,
    slug: getVal(mosObj.objSlug) || '',
    mosAbstract: getVal(mosObj.mosAbstract) || '',
    objPaths: getVal(mosObj.objPaths) || null,
    automationStart: mosExternalMetadata.mosPayload.automationStart || '00:00:00:00',
    automationDuration: mosExternalMetadata.mosPayload.automationDuration || '00:00:00:00',
    subtype: mosExternalMetadata.mosPayload.subtype || '',
    layer: mosExternalMetadata.mosPayload.layer
  };
};

const parseItem = itemNode => {
  if (!itemNode) {
    return;
  }

  const item = getVal(itemNode);

  const aStart = getVal(itemNode.itemEdStart);
  const aDur = getVal(itemNode.itemEdDur);

  const automationStart = TimecodeLib.init({ framerate: '29.97', timecode: '00:00:00:00' });
  const automationDuration = TimecodeLib.init({ framerate: '29.97', timecode: '00:00:00:00' });

  if (aStart) {
    automationStart.add(parseInt(aStart, 10));
  }
  if (aDur) {
    automationDuration.add(parseInt(aDur, 10));
  }

  return {
    itemId: getVal(item.itemID) || null,
    slug: getVal(item.itemSlug) || '',
    objId: getVal(item.objID) || null,
    objPaths: getVal(item.objPaths) || null,
    mosAbstract: getVal(item.mosAbstract) || '',
    automationStart: automationStart.toString(),
    automationDuration: automationDuration.toString()
  };
};

export const getMosObjIdFromObjId = objId => {
  const reg = /([^\\]\d+$)/g;
  const matches = reg.exec(objId);
  return matches && matches.length > 0 ? matches[0] : '';
};



// WEBPACK FOOTER //
// ./app/util/mosUtils.js